/* ===========================================================================
   BB SUMMARY POPUP — Box Summary items open the product popup
   ---------------------------------------------------------------------------
   QA tracker row 17 ("customers have actually requested this before").
   Clicking an item's photo or name in the Box Summary (sidebar on desktop,
   "View my box" drawer on mobile) opens the same product popup as the
   product grid. The quantity / remove buttons on the line keep working as
   before. Keyboard: the name is focusable; Enter or Space opens it.

   How: box-builder.js already opens the popup for any `bb:product-modal`
   event, so this only listens for clicks on summary lines and sends that
   event. Line styling (pointer cursor, underline on hover) is in
   assets/bb-qa-overrides.css.

   Mobile detail: box-builder.js closes the "View my box" drawer on any tap
   outside it, and the popup sits outside it, so a tap inside the popup used
   to close the drawer behind it. The popup is therefore marked as part of
   the drawer for that check only (data-bb-sidebar-toggle — the attribute
   that check already skips; box-builder.js binds the real toggle buttons
   once at load, so marking the popup later adds no toggle behavior).
   Closing the popup returns the shopper to their box summary.

   Step 4 Review (QA tracker row 42, added later on 2026-10-22): clicking an
   item in "Items in Your Box" opens the same popup in view-only mode. The
   quantity / Add to Box footer and "Pairs Well With" are hidden there, since
   the box is already finished; the popup returns to normal when it closes.
   Review items carry no product id in their markup, so each one is matched
   to the box contents in the order step 4 draws them.

   Pairs Well With (2026-10-22): suggestions that aren't in the builder's
   in-stock catalog are dropped, so a sold-out item set to keep selling at
   zero isn't offered as a pairing.

   Self-contained override: removing its <script> line in
   snippets/bb-assets.liquid fully reverts it. Added 2026-10-22.
   =========================================================================== */
(function () {
  "use strict";
  if (window.__bbSummaryPopup) return;
  window.__bbSummaryPopup = true;

  var HIT = "[data-bb-lines] .bb-line .bb-line__media, [data-bb-lines] .bb-line .bb-line__body";

  var REVIEW_ITEM = "[data-bb-review-items] .bb-review-item";
  var VIEW_ONLY = "is-view-only";

  function modalEl() {
    return document.querySelector("[data-bb-modal]");
  }

  function setViewOnly(on) {
    var modal = modalEl();
    if (modal) modal.classList.toggle(VIEW_ONLY, !!on);
  }

  function openModal(variantId, source, viewOnly) {
    if (!variantId) return;
    setViewOnly(viewOnly);
    document.dispatchEvent(
      new CustomEvent("bb:product-modal", {
        detail: { variantId: variantId, source: source || "goodies" }
      })
    );
    /* The popup skips items it can't show (e.g. sold out since they were
       added); don't leave the view-only flag behind if it stayed closed. */
    var modal = modalEl();
    if (viewOnly && modal && modal.hidden) setViewOnly(false);
  }

  function openFromLine(line) {
    if (!line) return;
    openModal(line.getAttribute("data-variant-id"), line.getAttribute("data-bb-source"), false);
  }

  /* Step 4 draws one .bb-review-item per box item, in the order of the
     box's items; match the clicked one by position. */
  function reviewVariantId(item) {
    var grid = item.parentElement;
    if (!grid || !window.BBPricing || !window.BBPricing.summarize) return null;
    var index = Array.prototype.indexOf.call(grid.querySelectorAll(".bb-review-item"), item);
    var items = (window.BBPricing.summarize().state || {}).items || {};
    var key = Object.keys(items)[index];
    return key && items[key] ? items[key].variantId : null;
  }

  function openFromReview(item) {
    if (!item) return;
    openModal(reviewVariantId(item), "goodies", true);
  }

  document.addEventListener("click", function (event) {
    if (!event.target.closest) return;
    var hit = event.target.closest(HIT);
    if (hit) {
      openFromLine(hit.closest(".bb-line"));
      return;
    }
    var review = event.target.closest(REVIEW_ITEM);
    if (review) openFromReview(review);
  });

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (!event.target.closest) return;
    var body = event.target.closest("[data-bb-lines] .bb-line .bb-line__body");
    if (body && event.target === body) {
      event.preventDefault();
      openFromLine(body.closest(".bb-line"));
      return;
    }
    var review = event.target.closest(REVIEW_ITEM);
    if (review && event.target === review) {
      event.preventDefault();
      openFromReview(review);
    }
  });

  /* Review items are redrawn on every change; make each one a focusable
     control with a clear label. */
  function decorateReview(grid) {
    grid.querySelectorAll(".bb-review-item").forEach(function (item) {
      if (item.hasAttribute("tabindex")) return;
      var title = item.querySelector(".bb-review-item__title");
      item.setAttribute("tabindex", "0");
      item.setAttribute("role", "button");
      item.setAttribute("aria-label", "View details: " + (title ? title.textContent : "item"));
    });
  }

  function watchReview() {
    var grid = document.querySelector("[data-bb-review-items]");
    if (!grid || grid.__bbReviewPopup) return;
    grid.__bbReviewPopup = true;
    decorateReview(grid);
    new MutationObserver(function () { decorateReview(grid); }).observe(grid, { childList: true });
  }

  /* Back to normal (footer + pairs shown) whenever the popup closes. */
  function watchModalClose() {
    var modal = modalEl();
    if (!modal || modal.__bbViewOnlyWatch) return;
    modal.__bbViewOnlyWatch = true;
    new MutationObserver(function () {
      if (modal.hidden) setViewOnly(false);
    }).observe(modal, { attributes: true, attributeFilter: ["hidden"] });
  }

  /* Summary lines are re-rendered on every change; make each name a
     focusable control with a clear label. */
  function decorate(list) {
    list.querySelectorAll(".bb-line").forEach(function (line) {
      var body = line.querySelector(".bb-line__body");
      if (!body || body.hasAttribute("tabindex")) return;
      var title = line.querySelector(".bb-line__title");
      body.setAttribute("tabindex", "0");
      body.setAttribute("role", "button");
      body.setAttribute("aria-label", "View details: " + (title ? title.textContent : "item"));
    });
  }

  function watchLists() {
    document.querySelectorAll("[data-bb-lines]").forEach(function (list) {
      if (list.__bbSummaryPopup) return;
      list.__bbSummaryPopup = true;
      decorate(list);
      new MutationObserver(function () { decorate(list); }).observe(list, { childList: true });
    });
  }

  function markPopup() {
    var modal = document.querySelector("[data-bb-modal]");
    if (modal && !modal.hasAttribute("data-bb-sidebar-toggle")) {
      modal.setAttribute("data-bb-sidebar-toggle", "");
    }
  }

  /* "Pairs Well With": only suggest items the builder can still add
     (2026-10-22). Build a Box items may be set to keep selling at zero so a
     box already in a cart can oversell, and Shopify's recommendations then
     still call a sold-out item available. Keep only suggestions that are in
     the builder's own catalog (its collections only hold items with stock)
     and not marked sold out; hide the row if none are left. */
  function filterPairs(list) {
    if (!window.BBCatalog || !window.BBCatalog.find) return;
    var removed = false;
    list.querySelectorAll(".bb-modal__pair").forEach(function (pair) {
      var btn = pair.querySelector("[data-variant-id]");
      var id = btn && btn.getAttribute("data-variant-id");
      var rec = id ? window.BBCatalog.find(id) : null;
      if (!rec || rec.available === false) {
        pair.parentNode.removeChild(pair);
        removed = true;
      }
    });
    if (removed && !list.querySelector(".bb-modal__pair")) {
      var wrap = list.closest("[data-bb-modal-pairs]");
      if (wrap) wrap.hidden = true;
    }
  }

  function watchPairs() {
    var list = document.querySelector("[data-bb-modal-pairs-list]");
    if (!list || list.__bbPairsFilter) return;
    list.__bbPairsFilter = true;
    new MutationObserver(function () { filterPairs(list); }).observe(list, { childList: true });
  }

  function boot() {
    watchLists();
    markPopup();
    watchReview();
    watchModalClose();
    watchPairs();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
